import { PocBadge } from 'commons/components/components-wrapper/poc-badge.component';
import { CustomTableConfig } from 'commons/components';
import { useUserPreferencesContext } from 'core/contexts/user-preferences/hooks/use-user-preferences-context.hook';
import { useTranslation } from 'core/i18n/i18n';
import { formatDateTime } from 'core/utils/dates';
import { getBasicColumnConfig } from 'core/utils/format-table-cell';
import { EditableColumn } from 'modules/global/model';
import { DeviceConfigurationInfo } from '../settings-devices-configuration-list.model';
import { DeviceConfigurationNameCellStyled } from '../settings-devices-configuration-list.style';

export const useDefaultConfigurationList = () => {
    const { t } = useTranslation();
    const { userLanguage } = useUserPreferencesContext();

    const getHeaderColumn = (column: string) => `settings.devices.device-settings.device-config-list.${column}`;

    const getColumnsToShow = (): EditableColumn[] => [
        {
            field: 'name',
            text: t('settings.devices.device-settings.device-config-list.name'),
            visible: true,
            pinned: false,
            columnIndex: 0,
        },
        {
            field: 'description',
            text: t('settings.devices.device-settings.device-config-list.description'),
            visible: true,
            pinned: false,
            columnIndex: 1,
        },
        {
            field: 'devicesAssigned',
            text: t('settings.devices.device-settings.device-config-list.devicesAssigned'),
            visible: true,
            pinned: false,
            columnIndex: 2,
        },
        {
            field: 'lastModified',
            text: t('settings.devices.device-settings.device-config-list.lastModified'),
            visible: true,
            pinned: false,
            columnIndex: 3,
        },
        {
            field: 'status',
            text: t('settings.devices.device-settings.device-config-list.status'),
            visible: true,
            pinned: false,
            columnIndex: 4,
        },
    ];
    const getDefaultConfig = (): CustomTableConfig<DeviceConfigurationInfo> => ({
        entity: 'deviceConfiguration',
        columnsToShow: getColumnsToShow(),
        fields: [
            {
                name: 'name',
                headerOptions: { text: t('settings.devices.device-settings.device-config-list.name'), sortable: false },
                rowOptions: {
                    cellOptions: {
                        className: 'full_width',
                        format: (data: DeviceConfigurationInfo) => (
                            <DeviceConfigurationNameCellStyled>
                                {data.name}
                                {data.isByDefault && (
                                    <PocBadge status="active" size="small">
                                        {t('settings.devices.device-settings.device-config-list.default')}
                                    </PocBadge>
                                )}
                            </DeviceConfigurationNameCellStyled>
                        ),
                    },
                },
            },
            getBasicColumnConfig(t, getHeaderColumn, 'description', false),
            getBasicColumnConfig(t, getHeaderColumn, 'devicesAssigned', false),
            {
                name: 'lastModified',
                headerOptions: {
                    text: t('settings.devices.device-settings.device-config-list.lastModified'),
                    sortable: false,
                },
                rowOptions: {
                    cellOptions: {
                        format: (data: DeviceConfigurationInfo) => `${formatDateTime(data.lastModified, userLanguage)}`,
                    },
                },
            },
            {
                name: 'status',
                headerOptions: {
                    text: t('settings.devices.device-settings.device-config-list.status'),
                    sortable: false,
                },
                rowOptions: {
                    cellOptions: {
                        format: (data: DeviceConfigurationInfo) =>
                            data.active
                                ? t('settings.devices.device-settings.device-config-list.status.active')
                                : t('settings.devices.device-settings.device-config-list.status.inactive'),
                    },
                },
            },
        ],
    });

    return { getDefaultConfig };
};
